import * as THREE from 'three';
import { getGlowTexture } from './style.js';

// Geometries are shared by every explosion.
const GEOMETRY = {
  // Sparks are thin rods along +Z so they can be pointed along their velocity
  spark: new THREE.BoxGeometry(0.08, 0.08, 1).translate(0, 0, -0.5),
  core: new THREE.IcosahedronGeometry(1.4, 2),
  shell: new THREE.IcosahedronGeometry(2, 2),
  shockwave: new THREE.RingGeometry(0.85, 1, 48, 1).rotateX(-Math.PI / 2),
  halo: new THREE.TorusGeometry(1.8, 0.08, 6, 40),
  debris: new THREE.OctahedronGeometry(0.22, 0)
};

const SPARK_COLORS = [0xffffff, 0x9be9ff, 0x5fd8ff, 0xb79bff, 0xffe4a8];

function glowMaterial(color, opacity = 1) {
  return new THREE.MeshBasicMaterial({
    color: new THREE.Color(color).multiplyScalar(1.4),
    transparent: true,
    opacity,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    toneMapped: false
  });
}

// An energy blast: a white-hot flash, an expanding plasma shell, a shockwave
// racing across the ground, a tilted energy ring, and sparks flung outwards.
export class Explosion {
  constructor(scene, position, onComplete) {
    this.scene = scene;
    this.position = new THREE.Vector3().copy(position);
    this.onComplete = onComplete;
    this.objects = [];
    this.sparks = [];
    this.debris = [];
    this.startTime = performance.now();
    this.lastTime = this.startTime;
    this.duration = 900;
    this.isComplete = false;
    this.create();
  }

  add(object) {
    this.scene.add(object);
    this.objects.push(object);
    return object;
  }

  create() {
    const p = this.position;

    // Sparks share a material per colour and fade together
    const colorFor = new Map();
    for (let i = 0; i < 26; i++) {
      const color = SPARK_COLORS[i % SPARK_COLORS.length];
      if (!colorFor.has(color)) colorFor.set(color, glowMaterial(color));
      const spark = this.add(new THREE.Mesh(GEOMETRY.spark, colorFor.get(color)));
      spark.position.copy(p);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 1.6 - 0.6); // Biased upwards
      const speed = 12 + Math.random() * 18;
      spark.userData.velocity = new THREE.Vector3(
        Math.sin(phi) * Math.cos(theta) * speed,
        Math.cos(phi) * speed,
        Math.sin(phi) * Math.sin(theta) * speed
      );
      spark.userData.length = 0.8 + Math.random() * 1.4;
      this.sparks.push(spark);
    }
    this.sparkMaterials = [...colorFor.values()];

    // Glowing hull fragments that tumble and fall
    this.debrisMaterial = glowMaterial(0x7fdcff, 1);
    for (let i = 0; i < 8; i++) {
      const piece = this.add(new THREE.Mesh(GEOMETRY.debris, this.debrisMaterial));
      piece.position.copy(p);
      const a = Math.random() * Math.PI * 2;
      const speed = 5 + Math.random() * 7;
      piece.userData.velocity = new THREE.Vector3(Math.cos(a) * speed, 6 + Math.random() * 8, Math.sin(a) * speed);
      piece.userData.spin = new THREE.Vector3(Math.random() * 8, Math.random() * 8, Math.random() * 8);
      this.debris.push(piece);
    }

    // White-hot core and the plasma shell around it
    this.core = this.add(new THREE.Mesh(GEOMETRY.core, glowMaterial(0xffffff)));
    this.core.position.copy(p);
    this.shell = this.add(new THREE.Mesh(GEOMETRY.shell, glowMaterial(0x4fc8ff, 0.6)));
    this.shell.position.copy(p);

    // A big soft flash so the blast reads from far away
    this.flash = this.add(new THREE.Sprite(new THREE.SpriteMaterial({
      map: getGlowTexture(),
      color: 0xbfeeff,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      toneMapped: false
    })));
    this.flash.position.copy(p);
    this.flash.scale.setScalar(9);

    // A tilted energy ring spinning outwards from the centre
    this.ring = this.add(new THREE.Mesh(GEOMETRY.halo, glowMaterial(0x9b8bff, 0.9)));
    this.ring.position.copy(p);
    this.ring.rotation.set(Math.PI / 2 + (Math.random() - 0.5) * 0.8, (Math.random() - 0.5) * 0.8, 0);

    // Shockwave across the ground, skipped for blasts high in the air
    if (p.y < 6) {
      this.shockwave = this.add(new THREE.Mesh(GEOMETRY.shockwave, glowMaterial(0x5fd8ff, 0.9)));
      this.shockwave.position.set(p.x, 0.12, p.z);
    }
  }

  update() {
    if (this.isComplete) return false;

    const now = performance.now();
    const progress = Math.min((now - this.startTime) / this.duration, 1);

    if (progress >= 1) {
      this.remove();
      this.isComplete = true;
      if (this.onComplete) {
        this.onComplete();
      }
      return false;
    }

    const deltaTime = Math.min((now - this.lastTime) / 1000, 0.05);
    this.lastTime = now;
    const ease = 1 - Math.pow(1 - progress, 3);

    // Sparks streak out, slowed by drag, and burn out over the first 70%
    const sparkLife = Math.min(progress / 0.7, 1);
    const tmp = new THREE.Vector3();
    for (const spark of this.sparks) {
      const v = spark.userData.velocity;
      spark.position.addScaledVector(v, deltaTime);
      v.y -= 14 * deltaTime;
      v.multiplyScalar(1 - 2.5 * deltaTime);
      spark.lookAt(tmp.copy(spark.position).add(v));
      spark.scale.set(1, 1, spark.userData.length * (0.3 + v.length() / 20) * (1 - sparkLife * 0.6));
    }
    for (const material of this.sparkMaterials) material.opacity = 1 - sparkLife;

    for (const piece of this.debris) {
      const v = piece.userData.velocity;
      piece.position.addScaledVector(v, deltaTime);
      v.y -= 25 * deltaTime;
      if (piece.position.y < 0.2) {
        piece.position.y = 0.2;
        v.set(v.x * 0.4, Math.abs(v.y) * 0.3, v.z * 0.4);
      }
      piece.rotation.x += piece.userData.spin.x * deltaTime;
      piece.rotation.y += piece.userData.spin.y * deltaTime;
    }
    this.debrisMaterial.opacity = 1 - progress;

    this.core.scale.setScalar(0.5 + ease * 1.2);
    this.core.material.opacity = Math.max(0, 1 - progress * 3.5);
    this.shell.scale.setScalar(0.6 + ease * 2.2);
    this.shell.material.opacity = 0.6 * Math.max(0, 1 - progress * 1.6);
    this.flash.material.opacity = Math.max(0, 1 - progress * 5);
    this.flash.scale.setScalar(9 + progress * 10);

    this.ring.scale.setScalar(0.4 + ease * 3.2);
    this.ring.rotation.z += 3 * deltaTime;
    this.ring.material.opacity = 0.9 * (1 - ease);

    if (this.shockwave) {
      const wave = Math.min(progress / 0.75, 1);
      const waveEase = 1 - Math.pow(1 - wave, 2);
      this.shockwave.scale.setScalar(1 + waveEase * 9);
      this.shockwave.material.opacity = 0.9 * (1 - wave);
    }

    return true;
  }

  remove() {
    // Shared geometries stay alive; only per-explosion materials are disposed
    const materials = new Set();
    for (const object of this.objects) {
      this.scene.remove(object);
      materials.add(object.material);
    }
    materials.forEach(material => material.dispose());
    this.objects = [];
    this.sparks = [];
    this.debris = [];
  }
}
